/**
 * Centralized fetch interceptor & auth guard.
 *
 * 1) At module init — synchronously checks JWT token expiry
 *    BEFORE React renders, preventing error flash on page refresh.
 *
 * 2) Monkey-patches `window.fetch` to intercept API responses and
 *    silently redirect to login on INVALID_TOKEN.
 *
 * Import this module once at the app entry point (src/index.tsx).
 */

import { checkTokenExpiry, clearAuthData } from "./authCheck";

// ── Fetch interceptor + pre-render auth guard ────────────────────

(function installFetchInterceptor() {
  const originalFetch = window.fetch;

  // Synchronous JWT expiry check BEFORE React renders.
  // If expired, clears auth data + redirects, AND sets flag so any
  // subsequent fetch calls (from React mount) are short-circuited.
  let isRedirecting = checkTokenExpiry();

  window.fetch = async function interceptedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    // If we're already redirecting, short-circuit any subsequent fetches
    // so the component never processes the response (no error flash).
    if (isRedirecting) {
      return new Promise<Response>(() => {});
    }

    const response = await originalFetch.call(window, input, init);

    // Only intercept calls to our API
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input instanceof Request
            ? input.url
            : "";

    if (!url.includes("/api/")) {
      return response;
    }

    // Check for INVALID_TOKEN — only redirect for this specific case,
    // not for generic 5xx errors (those might be transient server issues).
    try {
      const cloned = response.clone();
      const data = await cloned.json();

      if (data && data.success === false && data.msg === "INVALID_TOKEN") {
        isRedirecting = true;
        clearAuthData();
        window.location.replace("/auth-pages/login");
      }
    } catch {
      // Response is not JSON or not parseable — skip silently
    }

    return response;
  };
})();
